/**
 * Срезы статистики Яндекс.Директа (`ads_dim_*`) в ОТЧЁТЕ РЕКЛАМЫ (`/users/ad-reports`).
 * ТЗ бэка: `docs/FRONTEND_ADS_DIMS_TZ.md`, редакция 2.
 *
 * Отдельного узла у срезов НЕТ: бэк отдаёт их пунктами секции «Яндекс.Директ» в
 * `GET /utm-params/sections` (`params[].key` = `ads_dim_region`, `ads_dim_gender`, …), и фронт рисует
 * их обычным узлом «Параметры Яндекс.Директ» (`helpers/utils/utmSections.ts`) — список не хардкодится.
 * Ключ — одновременно dataKey группировки и id фильтра (lazylist через POST /filter/list-values
 * с `name` = ключ, отрицание — префикс `not_`).
 *
 * Здесь — только то, что зависит от ФОРМЫ ключа: разбор dataKey → id фильтра (переход
 * «строка отчёта → фильтр», цвет чипа), признак «рекламная ось» для правила первого уровня,
 * подсказки к срезам, на которых регистрации не считаются, и правило несовместимости
 * «Площадки» с остальными срезами в группировках.
 */

/** Префикс ключей срезов */
export const ADS_DIM_PREFIX = 'ads_dim_';

/**
 * Разбор ключа среза. Суффикс отчёта `_2`/`_3`/`_4` допускаем на случай сохранённых ранее осей
 * (как у UTM-осей), сам фронт оси с суффиксом не строит. Имя среза — буквы и подчёркивания
 * (`ads_dim_region_id`, `ads_dim_criteria_type`), цифр в нём нет — поэтому суффикс не «прилипает».
 */
const ADS_DIM_KEY_RE = /^(ads_dim_[a-z]+(?:_[a-z]+)*)(?:_([234]))?$/;

/**
 * Срезы, на которых регистрации и всё от них не считаются (Директ не передаёт их в метку) —
 * бэк отдаёт «-» в ячейках. Подсказка к пункту дерева, чтобы прочерки не выглядели ошибкой.
 */
const ADS_DIMS_WITHOUT_REGISTRATIONS: string[] = [ 'ads_dim_format', 'ads_dim_gender', 'ads_dim_age' ];

const WITHOUT_REGISTRATIONS_DESCRIPTION =
    'Директ не передаёт этот срез в метку, поэтому регистрации, заказы и платежи на нём не считаются («-»).';

/**
 * dataKey оси среза → id фильтра (равен ключу без суффикса отчёта).
 * @returns id фильтра либо undefined, если dataKey не относится к срезам
 */
export const resolveAdsDimFilterId = (dataKey?: string): string | undefined => {
    if (typeof dataKey !== 'string') return undefined;
    const match = dataKey.match(ADS_DIM_KEY_RE);
    return match ? match[1] : undefined;
};

/** Ключ относится к срезам Директа (по форме, без справочника) */
export const isAdsDimKey = (key?: string): boolean => resolveAdsDimFilterId(key) !== undefined;

/**
 * Подсказка к пункту дерева для среза без регистраций; для остальных ключей — undefined.
 * @param key ключ пункта секции (`params[].key`)
 */
export const getAdsDimDescription = (key: string): string | undefined =>
    ADS_DIMS_WITHOUT_REGISTRATIONS.includes(key) ? WITHOUT_REGISTRATIONS_DESCRIPTION : undefined;

/**
 * Срез «Площадка»: на бэке считается по ОТДЕЛЬНОЙ таблице статистики Директа, поэтому в
 * группировках не сочетается с остальными срезами `ads_dim_*` (с обычными рекламными осями
 * и параметрами меток — сочетается). Ключ зафиксирован на фронте (решение от 16.09.2026);
 * признака от бэка в справочнике нет.
 */
export const ADS_DIM_PLACEMENT_KEY = 'ads_dim_placement';

/** Текст для «Площадки», пока выбран другой срез Директа */
export const ADS_DIM_PLACEMENT_CONFLICT_MESSAGE =
    '«Площадка» считается отдельно и не сочетается с другими срезами Яндекс.Директа. '
    + 'Снимите выбранные срезы, чтобы выбрать её.';

/** Текст для остальных срезов, пока выбрана «Площадка» */
export const ADS_DIM_OTHER_CONFLICT_MESSAGE =
    'Этот срез не сочетается с «Площадкой»: она считается отдельно. '
    + 'Снимите «Площадку», чтобы выбрать его.';

/** Ключ (с суффиксом отчёта или без) — срез «Площадка» */
export const isAdsDimPlacementKey = (key?: string): boolean =>
    resolveAdsDimFilterId(key) === ADS_DIM_PLACEMENT_KEY;

/** Ключ — срез Директа, но не «Площадка» */
const isOtherAdsDimKey = (key?: string): boolean => isAdsDimKey(key) && !isAdsDimPlacementKey(key);

/**
 * Почему пункт-срез нельзя отметить при текущем выборе группировок.
 * Уже отмеченный пункт не блокируется никогда — иначе его нельзя было бы снять.
 * @param key ключ пункта дерева (равен dataKey)
 * @param selectedKeys ключи выбранных группировок
 * @returns текст сообщения либо undefined, если конфликта нет
 */
export const getAdsDimConflictMessage = (key: string, selectedKeys: string[]): string | undefined => {
    if (!isAdsDimKey(key) || selectedKeys.includes(key)) return undefined;
    if (isAdsDimPlacementKey(key)) {
        return selectedKeys.some(isOtherAdsDimKey) ? ADS_DIM_PLACEMENT_CONFLICT_MESSAGE : undefined;
    }
    return selectedKeys.some(isAdsDimPlacementKey) ? ADS_DIM_OTHER_CONFLICT_MESSAGE : undefined;
};

/** В наборе группировок «Площадка» стоит вместе с другим срезом Директа (недопустимый набор) */
export const hasAdsDimConflict = (selectedKeys: string[]): boolean =>
    selectedKeys.some(isAdsDimPlacementKey) && selectedKeys.some(isOtherAdsDimKey);
